
// apps/admin-ui/src/components/widgets/KycThroughput.tsx
// KYC Throughput widget — supervisor-level processed counts,
// per-CSR breakdown, and queue depth summary.
// ALL RIGHTS RESERVED

import { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QueueDepth {
  submitted: number;
  inReview: number;
  pendingKyc: number;
  needsInfo: number;
  amlFlagged: number;
  pendingSupervisor: number;
  approved: number;
  rejected: number;
}

interface Throughput {
  processedToday: number;
  processedThisWeek: number;
  processedThisMonth: number;
}

interface CsrEntry {
  csrId: string;
  csrName: string;
  processedToday: number;
  processedThisWeek: number;
  currentlyAssigned: number;
}

interface SupervisorStats {
  queue: QueueDepth;
  throughput: Throughput;
  csrBreakdown: CsrEntry[];
}

// ---------------------------------------------------------------------------
// Service URL
// ---------------------------------------------------------------------------

const KYC_URL = API_CONFIG.kyc;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function KycThroughput() {
  const [stats, setStats] = useState<SupervisorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${KYC_URL}/csr/supervisor/stats`, {
        headers: {
          'Content-Type': 'application/json',
          // In dev mode, pass supervisor credentials
          'x-dev-employee-id': '1000000002',
          'x-dev-employee-role': 'supervisor',
          'x-dev-employee-roles': JSON.stringify(['csr_supervisor']),
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch KYC supervisor stats:', err);
      setError(err.message || 'Failed to load');
      // Provide meaningful fallback for development
      setStats({
        queue: {
          submitted: 3,
          inReview: 2,
          pendingKyc: 1,
          needsInfo: 1,
          amlFlagged: 0,
          pendingSupervisor: 0,
          approved: 28,
          rejected: 4,
        },
        throughput: {
          processedToday: 6,
          processedThisWeek: 22,
          processedThisMonth: 84,
        },
        csrBreakdown: [
          { csrId: '1000000003', csrName: 'Charlie CSR', processedToday: 4, processedThisWeek: 14, currentlyAssigned: 2 },
          { csrId: '1000000004', csrName: 'Dana CSR', processedToday: 2, processedThisWeek: 8, currentlyAssigned: 1 },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        Loading KYC throughput data...
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-error)' }}>Failed to load KYC throughput data</p>
        <button onClick={fetchStats} style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem' }}>
          Retry
        </button>
      </div>
    );
  }

  const actionableCount = stats.queue.submitted + stats.queue.inReview
    + stats.queue.pendingKyc + stats.queue.needsInfo
    + stats.queue.amlFlagged + stats.queue.pendingSupervisor;

  return (
    <div>
      {/* ── Throughput Summary ─────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1.25rem',
      }}>
        <ThroughputCard label="Processed Today" value={stats.throughput.processedToday} color="#22c55e" />
        <ThroughputCard label="This Week" value={stats.throughput.processedThisWeek} color="#3b82f6" />
        <ThroughputCard label="This Month" value={stats.throughput.processedThisMonth} color="#8b5cf6" />
      </div>

      {/* ── Queue Depth ───────────────────────────────────── */}
      <div style={{
        marginBottom: '1.25rem',
        padding: '0.75rem 1rem',
        background: actionableCount > 10
          ? 'rgba(251, 191, 36, 0.08)'
          : 'rgba(34, 197, 94, 0.06)',
        borderRadius: '8px',
        border: `1px solid ${actionableCount > 10 ? 'rgba(251, 191, 36, 0.2)' : 'rgba(34, 197, 94, 0.15)'}`,
      }}>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-text-muted)',
          marginBottom: '0.5rem',
        }}>
          Queue Depth
        </div>
        <div style={{
          display: 'flex',
          gap: '1.25rem',
          flexWrap: 'wrap',
          fontSize: '0.8125rem',
        }}>
          <QueueBadge label="Submitted" count={stats.queue.submitted} color="#f59e0b" />
          <QueueBadge label="In Review" count={stats.queue.inReview} color="#3b82f6" />
          <QueueBadge label="Pending KYC" count={stats.queue.pendingKyc} color="#8b5cf6" />
          <QueueBadge label="Needs Info" count={stats.queue.needsInfo} color="#f97316" />
          <QueueBadge label="AML Flagged" count={stats.queue.amlFlagged} color="#ef4444" />
          <QueueBadge label="Supervisor" count={stats.queue.pendingSupervisor} color="#ec4899" />
          <span style={{ borderLeft: '1px solid var(--color-border)', margin: '0 0.25rem' }} />
          <QueueBadge label="Approved" count={stats.queue.approved} color="#22c55e" />
          <QueueBadge label="Rejected" count={stats.queue.rejected} color="#6b7280" />
        </div>
      </div>

      {/* ── Per-CSR Breakdown ─────────────────────────────── */}
      {stats.csrBreakdown.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--color-text-muted)',
            marginBottom: '0.5rem',
          }}>
            Per-CSR Breakdown
          </div>
          <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={thStyle}>Agent</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Today</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>This Week</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {stats.csrBreakdown.map((csr) => (
                <tr key={csr.csrId} style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500 }}>{csr.csrName}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {csr.processedToday}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {csr.processedThisWeek}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {csr.currentlyAssigned > 0 ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: '#3b82f6',
                        fontWeight: 500,
                      }}>
                        <span style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'inline-block',
                        }} />
                        {csr.currentlyAssigned}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────── */}
      {error && (
        <div style={{
          marginTop: '0.75rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid var(--color-border)',
          fontSize: '0.6875rem',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}>
          Using fallback data — live endpoint unavailable
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ThroughputCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: '0.75rem',
      background: `${color}10`,
      borderRadius: '8px',
    }}>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{label}</div>
    </div>
  );
}

function QueueBadge({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
      <span style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: count > 0 ? color : 'var(--color-border)',
        display: 'inline-block',
        flexShrink: 0,
      }} />
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: count > 0 ? color : 'var(--color-text-muted)' }}>
        {count}
      </span>
    </span>
  );
}

// Table styles
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
};

export default KycThroughput;

